import { useState, useEffect } from 'react';
import { Box, Radio, RadioGroup, Stack, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CustomFormControlLabel, HeadingText, TooltipContainer } from './style';

const GROUP_EMAIL = 'GROUP_EMAIL';
const SEPARATE_EMAIL = 'SEPARATE_EMAIL';

const TOOLTIP_STYLES = {
  zIndex: 1300,
  '& .MuiTooltip-tooltip': {
    boxShadow:
      'rgba(0, 0, 0, 0.2) 0px 10px 15px 3px, rgba(0, 0, 0, 0.2) 0px 4px 6px -2px',
    minWidth: 100,
    maxHeight: 300,
    borderRadius: '15px',
  },
} as const;

type EmailConfigType = typeof GROUP_EMAIL | typeof SEPARATE_EMAIL;

interface Props {
  selected?: boolean;
  onChangeCB?: (isSingleEmailThread: boolean) => void;
  isWorkflow?: boolean;
}

interface EmailOption {
  value: EmailConfigType;
  label: string;
  tooltipContent?: string | ((isWorkflow: boolean) => string);
}

const emailOptions: EmailOption[] = [
  {
    value: GROUP_EMAIL,
    label: 'Group emails together in a single thread',
    tooltipContent: (isWorkflow: boolean) =>
      `Group all notification emails ${
        isWorkflow ? 'from this workflow' : 'from this template'
      } into a single thread.`,
  },
  {
    value: SEPARATE_EMAIL,
    label: 'Keep emails as separate notifications',
    tooltipContent: 'Send each notification as an individual email.',
  },
];

const EmailConfigurations = (props: Props) => {
  const { selected, onChangeCB, isWorkflow } = props;
  const [isSingleEmailThread, setIsSingleEmailThread] = useState(
    selected ?? true,
  );
  const [emailConfig, setEmailConfig] = useState<EmailConfigType>(
    isSingleEmailThread ? GROUP_EMAIL : SEPARATE_EMAIL,
  );

  useEffect(() => {
    setIsSingleEmailThread(selected ?? true);
    setEmailConfig(selected ?? true ? GROUP_EMAIL : SEPARATE_EMAIL);
  }, [selected]);

  const handleConfigChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value as EmailConfigType;
    const newIsSingleEmailThread = newValue === GROUP_EMAIL;
    setEmailConfig(newValue);
    setIsSingleEmailThread(newIsSingleEmailThread);
    onChangeCB?.(newIsSingleEmailThread);
  };

  const renderTooltip = (content: string) => (
    <TooltipContainer>{content}</TooltipContainer>
  );

  return (
    <Box>
      <HeadingText>Configuration</HeadingText>
      <RadioGroup
        name="email-configuration"
        value={emailConfig}
        onChange={handleConfigChange}
        aria-label="Email configuration options"
      >
        {emailOptions.map((option) => (
          <CustomFormControlLabel
            key={option.value}
            sx={{ marginBottom: '-9px' }}
            value={option.value}
            control={<Radio />}
            label={
              <Stack direction="row" alignItems="center" spacing={1}>
                {option.label}
                <Tooltip
                  arrow
                  placement="top"
                  PopperProps={{ sx: TOOLTIP_STYLES }}
                  title={renderTooltip(
                    typeof option.tooltipContent === 'function'
                      ? option.tooltipContent(isWorkflow ?? false)
                      : option.tooltipContent || '',
                  )}
                >
                  <InfoOutlinedIcon
                    sx={{
                      fontSize: 18,
                      color: 'rgba(66, 66, 66, 1)',
                      marginLeft: '5px',
                    }}
                  />
                </Tooltip>
              </Stack>
            }
          />
        ))}
      </RadioGroup>
    </Box>
  );
};

export default EmailConfigurations;
